'use server';

import { cookies } from 'next/headers';
import { executeInvestigatorRevalidation } from './investigator-revalidation';
import type { InvestigatorRevalidationDescriptor } from './investigator-profile-cache';

// ─── Browser-triggered revalidation, as a Server Action ─────────────────────
//
// This runs INSIDE the Next.js server. It is invoked from client components via
// the page route (which Apache proxies to Next), so it sidesteps the Apache
// `/api/* -> NestJS` rule that hijacked (404) the old browser fetch to the
// `/api/investigadores/.../revalidate` route handlers. Deploy automation keeps
// using the HTTP route (server-to-server, direct to :3000).

function getApiBaseUrl() {
	const apiBaseUrl = process.env.INTERNAL_API_URL || process.env.NEXT_PUBLIC_API_URL;

	if (apiBaseUrl) return apiBaseUrl;
	if (process.env.NODE_ENV !== 'production') return 'http://localhost:3001';

	throw new Error('Missing INTERNAL_API_URL or NEXT_PUBLIC_API_URL for investigator revalidation action');
}

/**
 * Revalidate the investigator cache surfaces described by `descriptor`.
 *
 * Requires a valid session (verified against the API) so a crafted call cannot
 * churn the cache. Revalidation only re-reads public, idempotent data and is not
 * a mutation, so any authenticated user may trigger it — ownership is not checked
 * per profile. Best-effort by contract: callers treat a falsy result as "cache
 * may be stale", never as a mutation failure.
 */
export async function revalidateInvestigatorTargetsAction(
	descriptor: InvestigatorRevalidationDescriptor,
): Promise<{ ok: boolean }> {
	const cookieStore = await cookies();
	const token = cookieStore.get('access_token')?.value;
	if (!token) return { ok: false };

	try {
		const probe = await fetch(`${getApiBaseUrl()}/auth/me`, {
			headers: { Cookie: `access_token=${token}` },
			cache: 'no-store',
		});
		if (!probe.ok) return { ok: false };
	} catch {
		return { ok: false };
	}

	// `allProfiles` (bust every profile) is reserved for server automation with the deploy
	// secret — a browser session never gets it, whatever the descriptor says.
	executeInvestigatorRevalidation({ ...descriptor, allProfiles: false });
	return { ok: true };
}
